import { SHARED_BRAND_TOTAL } from '../elements/constants';
import { DataCellValue, DataRow, ListBoxMenuItem } from '../types';
import { BRAND_KEY, BRAND_OWNER_DELIMITER, BRAND_OWNER_GROUP_PREFIX, BRAND_OWNER_KEY } from './brandOwner.constants';

/**
 * Extracts brand and brand owner from a string into an object.
 * The string should contain brand and brand owner separated by a delimiter.
 * @param items the string containing brand and brand owner separated by a delimiter
 * @returns brand and brand owner as an object
 */
export const getBrandAndBrandOwner = (items: string) => {
  const [brand, brandOwner] = items ? items.split(BRAND_OWNER_DELIMITER) : [null, null];
  const brandName = brand === 'null' ? null : brand;
  const brandOwnerName = brandOwner === 'null' ? null : brandOwner;
  return {
    brand: brandName,
    brandOwner: brandOwnerName,
  };
};

/**
 * Extracts brand and brand owner from a string into an object.
 * The string should contain brand and brand owner separated by a delimiter.
 * @param items the string containing brand and brand owner separated by a delimiter
 * @returns brand or brand owner array for each selected items
 */
export const getBrandOrBrandOwner = (items: string): string => {
  const [brand, brandOwner] = items ? items.split(BRAND_OWNER_DELIMITER) : [null, null];
  const brandName = brand === 'null' ? null : brand;
  const brandOwnerName = brandOwner === 'null' ? null : brandOwner;
  if (brandName) {
    return brandName;
  }
  return brandOwnerName;
};

/**
 * Extracts a list of brands from an array of strings.
 * Each string should contain a brand and brand owner separated by a delimiter.
 * @param items list of strings containing brand and brand owner separated by a delimiter
 * @returns only the brand names from the list
 */
export const getBrandList = (items: string[]) => {
  return items?.map(item => {
    const [brand, brandOwner] = item.split(BRAND_OWNER_DELIMITER);
    return brandOwner ? brand : item;
  });
};

/**
 * Checks if the selected item is a brand owner group.
 * @param items list of strings containing brand and brand owner separated by a delimiter
 * @param selectedItem the selected item containing brand and brand owner information
 * @returns boolean indicating if the selected item is a brand owner group
 */
export const isBrandOwnerGroup = (items: string[], selectedItem: Record<string, DataCellValue>) => {
  const brandOwner = selectedItem?.[BRAND_OWNER_KEY];
  const brand = selectedItem?.[BRAND_KEY];

  if (!brandOwner || !brand) {
    return false;
  }
  // If the brand owner is the same as the brand, it is a brand owner group
  if (brandOwner === brand) {
    return items.includes(`${BRAND_OWNER_GROUP_PREFIX}${selectedItem[BRAND_OWNER_KEY]}`);
  }
  return false;
};

/**
 * Checks if the selected brand or brand owner is in the provided list.
 * Used in makeIndicatorInfo to filter valid rows based on the selected brand or brand owner.
 * @param unformattedBrandList list of strings containing brand and brand owner separated by a delimiter
 * @param row the selected row containing brand and brand owner information
 * @param grouping key to access the brand or brand owner in the row
 * @returns boolean indicating if the selected brand or brand owner is in the list
 */
export const isSelectedBrandOrBrandOwnerInList = (
  row: DataRow,
  grouping: string,
  unformattedBrandList: string[] = []
) => {
  const selectedRowValue = String(row.values[grouping]);
  const brandList = getBrandList(unformattedBrandList);
  return brandList?.includes(selectedRowValue) || isBrandOwnerGroup(unformattedBrandList, row.values);
};

/**
 * Checks if the focus type of the selection is a brand owner group.
 * Eg: 'BEKO GROUP|brand_owner:BEKO GROUP'
 * @param selection the selected item containing brand and brand owner information
 * @returns boolean indicating if the selection is a brand owner group
 */
export const isFocusBrandOwnerGroup = (selection: string) => {
  const { brand, brandOwner } = getBrandAndBrandOwner(selection);
  if (!brand || !brandOwner) {
    return false;
  }
  return brand === brandOwner;
};

/**
 * Appends '(Total)' to the brand owner label if it is a shared brand.
 * @param paramValue paramValue to check whether it is a shared brand
 * @param sharedBrands list of shared brands
 * @returns formatted paramValue with or without the '(total)' appended
 */
export const formatSharedBrandsLabel = (item: ListBoxMenuItem, sharedBrands: string[]): string => {
  const { brand } = getBrandAndBrandOwner(item.value as string);
  if (sharedBrands.includes(brand)) {
    return `${brand}${SHARED_BRAND_TOTAL}`;
  }
  return item.label;
};
